/* eslint-disable sonarjs/cognitive-complexity */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { differenceInCalendarDays } from 'date-fns';
import { useMemo, useState } from 'react';
import type { Document, PaginatedDocumentList, PaginatedDocumentTypeList } from '../../../js/api';
import {
  DocumentsService,
  DocumentStatusEnum,
  OwnerTypeEnum,
  SignatureRequestStatusEnum,
} from '../../../js/api';
import { useCodeDefinitionsAsMap } from '../hooks/useCodeDefinitions';
import { useDocumentSelection } from '../hooks/useDocumentSelection';
import { useHasPermission } from '../hooks/useHasPermission';
import {
  formatApiDate,
  formatDate,
  formatDateTo24Hr,
  formatFullTimestamp,
  getDateTemporalityClass,
  parseApiLocalDate,
} from '../utils/date';
import { pickableUploadTypes } from '../utils/documentTypes';
import { fetchAllPages } from '../utils/pagination';
import { SIGNATURE_REQUEST_UPDATE_PERMISSION } from '../utils/permissions';
import { useAudience } from '../utils/workflowAccess';
import { Button } from './button';
import {
  DocumentActionDialogs,
  DocumentActionsMenu,
  DocumentQuickActions,
  documentLinkClasses,
  getDocumentCapabilities,
  pendingSignerInvites,
  useCanDownloadDocument,
  useDocumentActions,
} from './document-actions';
import { DocumentUploadDialog } from './document-upload-dialog';
import { Spinner } from './spinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './table';
import { TooltippedText } from './tooltipped-text';

// Re-exported: the helpers moved to document-actions with the rest of the action logic.
export { pendingSignerInvites, shouldPromptOrgSigner } from './document-actions';

export const DOCUMENTS_QUERY_KEY = 'documents';
export const DOCUMENT_TYPES_QUERY_KEY = 'documentTypes';

type DocumentsTabProps = {
  /** E.g. 'connections.connectedfacility', 'connections.clearance' */
  contextType: string;
  /** The entity ID to scope documents to */
  contextId: string;
  /** Who owns uploaded documents — typically the logged-in user's entity ID */
  ownerId: string;
  /** Optional provider ID for HCO-owned docs about a specific provider */
  relatedProviderId?: string;
  /**
   * Optional bulk-download action. When provided, renders a "Download all (.zip)"
   * button in the tab header. Omit to hide the button entirely.
   */
  bulkDownloadAction?: {
    onClick: () => void;
    isLoading: boolean;
    disabledReason?: string;
  };
  /**
   * Called after a successful upload or replace. Use this when uploading a
   * document can change the parent entity server-side (e.g. confirming a signed
   * ACL auto-confirms its assignment) so the parent can refetch its own queries.
   */
  onDocumentsChanged?: () => void;
  /**
   * Opt-in: render one table per document type under a titled section instead of a
   * single table. Off by default so existing drawers render exactly as before; the
   * staff provider drawer turns it on because a provider accumulates many types.
   */
  groupByType?: boolean;
  /**
   * Opt-in: a checkbox on every row the viewer may download or remove (and a select-all
   * per table) feeding the floating selection bar (`useDocumentSelection`): Download (one
   * selected, saved as itself; several, saved as one zip) and Remove. `zipSubject` names the zip: "John Joe MD
   * Documents" → "John Joe MD Documents 20261004-1532.zip". Off by default.
   */
  selectable?: { zipSubject: string };
};

export type DocumentTypeGroup = { title: string; documents: Document[] };

/** Documents bucketed by their type's title, groups and rows in a stable order. */
export function groupDocumentsByType(documents: Document[]): DocumentTypeGroup[] {
  const groups = new Map<string, Document[]>();
  documents.forEach((doc) => {
    const title = doc.document_type?.title || 'Other';
    groups.set(title, [...(groups.get(title) ?? []), doc]);
  });
  return [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([title, docs]) => ({ title, documents: docs }));
}

type StatusIndicator = {
  iconClassName: string;
  colorClassName: string;
  bgColorClassName: string;
  ariaLabel: string;
  tooltip: string;
};

function getStatusIndicator(
  status: Document['status'] | string | null | undefined
): StatusIndicator | null {
  switch (status) {
    case DocumentStatusEnum.PENDING:
      return {
        iconClassName: 'fas fa-exclamation-triangle',
        colorClassName: 'tw:text-stoplight-warning',
        bgColorClassName: 'tw:bg-stoplight-warning',
        ariaLabel: 'Pending',
        tooltip: 'Upload did not complete',
      };
    case DocumentStatusEnum.FAILED:
      return {
        iconClassName: 'fas fa-triangle-exclamation',
        colorClassName: 'tw:text-stoplight-danger',
        bgColorClassName: 'tw:bg-stoplight-danger',
        ariaLabel: 'Failed',
        tooltip: 'Upload failed',
      };
    case DocumentStatusEnum.PROCESSING:
      return {
        iconClassName: 'fas fa-circle-info',
        colorClassName: 'tw:text-blue-800',
        bgColorClassName: 'tw:bg-blue-800',
        ariaLabel: 'Processing',
        tooltip: 'Processing, check back later',
      };
    default:
      return null;
  }
}

function formatExpiresIn(expiresOn: string | null | undefined): string | null {
  const date = parseApiLocalDate(expiresOn);
  if (!date) return null;
  const days = differenceInCalendarDays(date, new Date());
  if (days < -1) return `${Math.abs(days)} days ago`;
  if (days === -1) return 'yesterday';
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  return `in ${days} days`;
}

// Chip styling per signature-request status. Labels come from the
// SignatureRequestStatus code definitions (never hardcoded).
function getSignatureBadgeClasses(status: string | undefined): string {
  switch (status) {
    case SignatureRequestStatusEnum.COMPLETED:
      return 'tw:bg-stoplight-success/15 tw:text-stoplight-success';
    case SignatureRequestStatusEnum.PARTIALLY_SIGNED:
      return 'tw:bg-stoplight-info/15 tw:text-stoplight-info';
    case SignatureRequestStatusEnum.VOIDED:
      return 'tw:bg-zinc-200 tw:text-zinc-500';
    case SignatureRequestStatusEnum.PENDING:
    default:
      return 'tw:bg-stoplight-warning/15 tw:text-stoplight-warning';
  }
}

export function DocumentsTab({
  contextType,
  contextId,
  ownerId,
  relatedProviderId,
  bulkDownloadAction,
  onDocumentsChanged,
  groupByType = false,
  selectable,
}: DocumentsTabProps) {
  const queryClient = useQueryClient();
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);

  const audience = useAudience();
  // Staff exceptions to the signature lock (replace/delete a signing or
  // executed document, request an external signature) need the
  // signature-request update code on top of the staff audience.
  const canUpdateSignature = useHasPermission(SIGNATURE_REQUEST_UPDATE_PERMISSION);
  const { data: signatureStatusLabels } = useCodeDefinitionsAsMap('SignatureRequestStatus');
  const { data: inviteStatusLabels } = useCodeDefinitionsAsMap('SignatureInviteStatus');

  // Every document on this context: the tab IS the list, so it walks every page rather
  // than showing the API's first 50 as if they were all of them (utils/pagination).
  const { data: documents, isLoading: isLoadingDocuments } = useQuery({
    queryKey: [DOCUMENTS_QUERY_KEY, contextId],
    queryFn: () =>
      fetchAllPages<Document>((page) =>
        DocumentsService.documentsList({ contextType, contextId, ...page })
      ),
    enabled: !!contextId,
    select: (data: PaginatedDocumentList) => data?.results ?? [],
  });

  // Fetch document types relevant to this context. The backend scopes to
  // types the user's audience has any permission on; we derive the
  // uploadable subset client-side from the audience arrays.
  const { data: documentTypes } = useQuery({
    queryKey: [DOCUMENT_TYPES_QUERY_KEY, contextType, audience],
    queryFn: () =>
      DocumentsService.documentsTypesList({
        contextTypes: [contextType],
      }),
    select: (data: PaginatedDocumentTypeList) => data?.results ?? [],
  });

  const uploadableTypes = useMemo(
    () => pickableUploadTypes(documentTypes ?? [], audience),
    [documentTypes, audience]
  );

  const invalidateDocuments = () =>
    queryClient.invalidateQueries({ queryKey: [DOCUMENTS_QUERY_KEY, contextId] });

  // Action menu state, mutations and dialogs are shared with the in-context
  // document views (document-actions.tsx) so the rules can never drift.
  const actions = useDocumentActions({
    invalidate: invalidateDocuments,
    onDocumentsChanged,
  });
  const viewer = { ownerId, audience, canUpdateSignature };

  // Selection (opt-in): rows the viewer may download or remove get a checkbox, in
  // display order (grouped order when grouping), so a zip lists them the way the tab
  // shows them. The bar downloads / zips the downloadable ones and removes the removable.
  const canDownload = useCanDownloadDocument(contextType);
  const canDelete = (doc: Document) => getDocumentCapabilities(doc, viewer).canDelete;
  const isSelectable = (doc: Document) => !!selectable && (canDownload(doc) || canDelete(doc));
  const displayedDocuments = useMemo(() => {
    const rows = documents ?? [];
    return groupByType ? groupDocumentsByType(rows).flatMap((group) => group.documents) : rows;
  }, [documents, groupByType]);
  const selectableIdsOf = (rows: Document[]) => rows.filter(isSelectable).map((doc) => doc.id);
  const downloadableIds = selectable
    ? displayedDocuments.filter(canDownload).map((doc) => doc.id)
    : [];
  const selection = useDocumentSelection({
    resetKey: `${contextType}|${contextId}`,
    selectableIds: selectableIdsOf(displayedDocuments),
    downloadableIds,
    zipSubject: selectable?.zipSubject ?? '',
    testIdPrefix: 'documents',
    bulkDelete: selectable
      ? {
          deletable: displayedDocuments.filter(canDelete),
          onDeleted: () => {
            invalidateDocuments();
            onDocumentsChanged?.();
          },
        }
      : undefined,
  });

  // "Download all" beside Upload: the host's own action when it supplies one (timecards),
  // else — in selectable mode — every downloadable document in the tab (one saves as
  // itself, several as a zip).
  const downloadAll =
    bulkDownloadAction ??
    (selectable
      ? {
          onClick: selection.downloadAll,
          isLoading: selection.isDownloading,
          disabledReason:
            downloadableIds.length === 0 ? 'No downloadable documents on file yet' : undefined,
        }
      : undefined);
  const downloadAllLabel = downloadableIds.length === 1 ? 'Download all' : 'Download all (.zip)';

  const hasUploadableTypes = (uploadableTypes?.length ?? 0) > 0;
  const canRenderUploadDialog = !!ownerId;
  const tableCellStyles = 'tw:!p-1';
  // Hide the status icon column entirely when no row needs an indicator,
  // so the table reclaims that horizontal space.
  const showStatusColumn = (documents ?? []).some(
    (doc: Document) => getStatusIndicator(doc.status) !== null
  );

  // The table body is unchanged by grouping; grouped mode just renders it once
  // per document type under a section header (which already names the type, so the
  // Type column goes). Selectable mode: the single table's select-all sits in its
  // column header; a grouped table's lives in the type band above it
  // (`renderGroupSelectAll`), so the header cell stays an empty spacer.
  const showTypeColumn = !groupByType;
  const renderGroupSelectAll = (rows: Document[], groupTitle: string) =>
    selection.renderGroupCheckbox(
      selectableIdsOf(rows),
      groupTitle,
      `documents-select-all-${groupTitle}`
    );
  const renderDocumentsTable = (rows: Document[], groupTitle?: string) => (
    <Table dense>
      <TableHead>
        <TableRow className="tw:hover:bg-transparent" noPointer>
          {selectable && (
            <TableHeader className={clsx(tableCellStyles, 'tw:w-px')}>
              {groupTitle === undefined && renderGroupSelectAll(rows, 'documents')}
            </TableHeader>
          )}
          {/* tw:relative: sr-only is position:absolute and a <th> is no containing block, so
              without it the hidden label escapes the drawer's scroll clip at its static page
              position and, on a long tab, makes the whole page taller (scrollable). */}
          <TableHeader className={clsx(tableCellStyles, 'tw:w-px tw:relative')}>
            <span className="tw:sr-only">Actions</span>
          </TableHeader>
          {showStatusColumn && <TableHeader className={clsx(tableCellStyles, 'tw:w-6')} />}
          <TableHeader className={clsx(tableCellStyles, 'tw:!pl-2 tw:w-px')}>Name</TableHeader>
          {showTypeColumn && (
            <TableHeader className={clsx(tableCellStyles, 'tw:w-px tw:!px-2')}>Type</TableHeader>
          )}
          <TableHeader className={clsx(tableCellStyles, 'tw:w-px')}>Uploaded</TableHeader>
          <TableHeader className={tableCellStyles}>Expiration</TableHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((doc: Document) => {
          const capabilities = getDocumentCapabilities(doc, viewer);
          const { signature } = capabilities;
          const signatureBadgeLabel = signature
            ? (signatureStatusLabels?.[signature.status] ?? signature.status)
            : null;
          const signerInvites = pendingSignerInvites(signature);
          const signatureBadgeTooltip = signerInvites.length ? (
            <div className="tw:flex tw:flex-col tw:items-start tw:gap-0.5">
              <span className="tw:font-medium">Invited to sign:</span>
              {signerInvites.map((invite) => (
                <span key={`${invite.signer_audience}:${invite.invited_email}`}>
                  {invite.invited_email} — {inviteStatusLabels?.[invite.status] ?? invite.status}{' '}
                  (sent {formatDateTo24Hr(invite.sent_at) || '-'}){invite.opened ? ' · opened' : ''}
                </span>
              ))}
            </div>
          ) : null;
          const statusIndicator = getStatusIndicator(doc.status);
          const expiresInLabel = formatExpiresIn(doc.expires_on);
          const expiresColorClass = getDateTemporalityClass(doc.expires_on);
          const documentTooltipContent = (
            <div className="tw:flex tw:flex-col tw:items-start">
              <div className="tw:text-[12px] tw:font-medium">{doc.document_type.title}</div>
              <div className="tw:text-[10px] tw:font-medium">{doc.document_type.description}</div>
            </div>
          );

          return (
            <TableRow key={doc.id} className="" noPointer>
              {selectable && (
                <TableCell className={clsx(tableCellStyles, 'tw:w-px')}>
                  {isSelectable(doc) &&
                    selection.renderCheckbox(doc.id, doc.label || doc.original_filename)}
                </TableCell>
              )}
              <TableCell className={clsx(tableCellStyles, 'tw:w-px')}>
                {/* Every action lives in the ⋮ menu; a signature document also gets its
                    everyday Sign / Request signature / Replace as one-click buttons. */}
                <span className="tw:flex tw:items-center tw:gap-1" data-testid="document-actions">
                  <DocumentActionsMenu
                    doc={doc}
                    capabilities={capabilities}
                    actions={actions}
                    quickActionsOutside
                    // the checkbox + selection bar already download a selectable row
                    hideDownload={!!selectable && canDownload(doc)}
                  />
                  <DocumentQuickActions doc={doc} capabilities={capabilities} actions={actions} />
                </span>
              </TableCell>

              {showStatusColumn && (
                <TableCell className={clsx(tableCellStyles, 'tw:w-6 tw:text-center')}>
                  {statusIndicator && (
                    <TooltippedText
                      tooltipContent={statusIndicator.tooltip}
                      tooltipClassName={statusIndicator.bgColorClassName}
                    >
                      <i
                        className={`${statusIndicator.iconClassName} ${statusIndicator.colorClassName}`}
                        aria-label={statusIndicator.ariaLabel}
                      />
                    </TooltippedText>
                  )}
                </TableCell>
              )}
              <TableCell className={clsx(tableCellStyles, 'tw:cursor-pointer tw:w-px')}>
                <span className="tw:inline-flex tw:items-center tw:gap-1.5">
                  <TooltippedText
                    tooltipContent={
                      <>
                        Original Filename:
                        <br />
                        {doc.original_filename}
                      </>
                    }
                  >
                    {capabilities.isFinalized ? (
                      // the name opens the document, the way the in-context views do
                      <button
                        type="button"
                        className={documentLinkClasses}
                        onClick={() => actions.openDocument(doc.id, 'inline')}
                        disabled={actions.isOpening}
                        title="View Document"
                        data-testid={`document-link-${doc.id}`}
                      >
                        {doc.label || doc.original_filename}
                      </button>
                    ) : (
                      doc.label || doc.original_filename
                    )}
                  </TooltippedText>
                  {signatureBadgeLabel && (
                    <TooltippedText tooltipContent={signatureBadgeTooltip}>
                      <span
                        className={clsx(
                          'tw:inline-flex tw:items-center tw:gap-1 tw:rounded-full tw:px-2 tw:py-0.5 tw:text-[10px] tw:font-medium tw:whitespace-nowrap',
                          getSignatureBadgeClasses(signature?.status)
                        )}
                        data-testid={`document-signature-badge-${doc.id}`}
                      >
                        <i className="fas fa-signature" />
                        {signatureBadgeLabel}
                      </span>
                    </TooltippedText>
                  )}
                </span>
              </TableCell>
              {showTypeColumn && (
                <TableCell className={clsx(tableCellStyles, 'tw:cursor-pointer tw:!px-2')}>
                  <TooltippedText tooltipContent={documentTooltipContent}>
                    {doc.document_type.code}
                  </TooltippedText>
                </TableCell>
              )}
              <TableCell className={clsx(tableCellStyles, 'tw:cursor-pointer')}>
                <TooltippedText
                  tooltipContent={
                    <div className="tw:flex tw:flex-col tw:items-start tw:gap-0.5">
                      <span>{formatFullTimestamp(doc.uploaded_at)}</span>
                      {doc.created_by_name && <span>By {doc.created_by_name}</span>}
                    </div>
                  }
                >
                  {/* short and year-aware ("Oct 4" / "Oct 4, 2025"); the tooltip has the rest */}
                  <span className="tw:whitespace-nowrap">
                    {formatDate(doc.uploaded_at, 'MMM d')}
                  </span>
                </TooltippedText>
              </TableCell>
              <TableCell className={tableCellStyles}>
                {expiresInLabel ? (
                  <TooltippedText
                    tooltipContent={
                      <div className="tw:flex tw:flex-col tw:gap-0.5">
                        <span>
                          Issued: {formatApiDate(doc.issued_on, { emptyLabel: 'Not specified' })}
                        </span>
                        <span>Expires: {formatApiDate(doc.expires_on)}</span>
                      </div>
                    }
                  >
                    <span className={expiresColorClass}>{expiresInLabel}</span>
                  </TooltippedText>
                ) : (
                  <span className="tw:text-zinc-400" />
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );

  return (
    <div className="tw:space-y-4 tw:mb-2">
      <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
        {hasUploadableTypes && canRenderUploadDialog && (
          <Button
            color="brand-green-outline"
            small
            onClick={() => setIsUploadDialogOpen(true)}
            data-testid="connection-documents-upload-button"
          >
            <i className="fas fa-upload" />
            Upload Document
          </Button>
        )}
        {downloadAll && (
          <Button
            color="brand-green-outline"
            small
            onClick={downloadAll.onClick}
            disabled={!!downloadAll.disabledReason || downloadAll.isLoading}
            title={downloadAll.disabledReason}
            className="tw:ml-auto"
            aria-label="Download all documents as zip"
            data-testid="documents-bulk-download-button"
          >
            {downloadAll.isLoading ? (
              <>
                <Spinner size="sm" variant="zinc" />
                <span>Preparing download…</span>
              </>
            ) : (
              <>
                <i className="fas fa-file-zipper" />
                <span>{bulkDownloadAction ? 'Download all (.zip)' : downloadAllLabel}</span>
              </>
            )}
          </Button>
        )}
      </div>

      {isLoadingDocuments ? (
        <div className="tw:text-sm tw:text-zinc-500">Loading documents...</div>
      ) : !documents || documents.length === 0 ? (
        <div className="tw:rounded-md tw:border tw:border-dashed tw:border-zinc-300 tw:p-6 tw:text-center tw:text-sm tw:text-zinc-500">
          No documents have been uploaded yet.
        </div>
      ) : groupByType ? (
        <div className="tw:flex tw:flex-col tw:gap-4" data-testid="documents-grouped">
          {groupDocumentsByType(documents).map((group) => (
            <section key={group.title} data-testid={`documents-group-${group.title}`}>
              <div className="tw:flex tw:items-center tw:gap-2 tw:mb-1 tw:px-2 tw:py-1 tw:rounded-md tw:bg-brand-green-3">
                {/* one click selects every downloadable document of this type */}
                {selectable && renderGroupSelectAll(group.documents, group.title)}
                {/* Bootstrap's reboot styles bare headings; force the size and margins. */}
                <h4 className="tw:!text-sm tw:!font-semibold tw:!leading-5 tw:!m-0 tw:text-brand-green-0">
                  {group.title}
                </h4>
                <span className="tw:text-xs tw:text-zinc-500">({group.documents.length})</span>
              </div>
              {renderDocumentsTable(group.documents, group.title)}
            </section>
          ))}
        </div>
      ) : (
        renderDocumentsTable(documents)
      )}
      {selectable && selection.bar}

      {canRenderUploadDialog && (
        <DocumentUploadDialog
          open={isUploadDialogOpen}
          onClose={() => setIsUploadDialogOpen(false)}
          onSuccess={() => {
            invalidateDocuments();
            onDocumentsChanged?.();
          }}
          ownerType={audience as OwnerTypeEnum}
          ownerId={ownerId}
          relatedProviderId={relatedProviderId}
          contextType={contextType}
          contextId={contextId}
        />
      )}

      <DocumentActionDialogs actions={actions} audience={audience} />
    </div>
  );
}
